import { useState } from "react";
import { formatDate } from "../../utils";
import { useGetUserCreditSpendsQuery } from "../../store/base-api";
import { CreditSpendDTO, getCreditSpendText } from "../../models";
import LoadingIndicator from "../loaders/loading-indicator";
import FormError from "../errors/form-error";

type CreditSpendsTableProps = { userId: string };

const CreditSpendsTable = ({ userId }: CreditSpendsTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { data, isLoading, error } = useGetUserCreditSpendsQuery({
    id: userId,
    currentPage,
    pageSize,
  });

  if (isLoading) return <LoadingIndicator />;
  if (error) return <FormError error={error} />;
  if (!data || !data.items.length)
    return <p className="text-black">No credit spends found.</p>;

  return (
    <div className="mb-8">
      <table className="min-w-full border border-slate-300 mb-2">
        <thead>
          <tr className="bg-slate-100">
            <th className="p-2 border">Created</th>
            <th className="p-2 border">Story Title</th>
            <th className="p-2 border">PostPaid Status</th>
            <th className="p-2 border">Type</th>
            <th className="p-2 border">Credits Spent</th>
            <th className="p-2 border">Is Studio</th>
            <th className="p-2 border">Story Type</th>
            <th className="p-2 border">Top Level Category</th>
            <th className="p-2 border">Slug</th>
            <th className="p-2 border">Draft ID</th>
            <th className="p-2 border">Web Story ID</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((spend: CreditSpendDTO) => (
            <tr key={spend.webStoryId + spend.created} className="border-t">
              <td className="p-2 border">{formatDate(spend.created)}</td>
              <td className="p-2 border">{spend.storyTitle}</td>
              <td className="p-2 border">{spend.creditSpendPostPaidStatus}</td>
              <td className="p-2 border relative group">
                <span className="cursor-help border-b border-dotted border-gray-500">
                  {getCreditSpendText
                    ? getCreditSpendText(spend.creditSpendType)
                    : spend.creditSpendType}
                </span>
                <div className="absolute z-10 invisible group-hover:visible bg-black text-white p-2 rounded text-xs mt-1 left-0">
                  Enum value: {spend.creditSpendType}
                </div>
              </td>
              <td className="p-2 border">{spend.creditsSpent}</td>
              <td className="p-2 border">{spend.isStudio ? "Yes" : "No"}</td>
              <td className="p-2 border">{spend.storyType}</td>
              <td className="p-2 border">{spend.topLevelCategory}</td>
              <td className="p-2 border">{spend.slug}</td>
              <td className="p-2 border">{spend.draftId ?? "-"}</td>
              <td className="p-2 border">{spend.webStoryId}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-end items-center gap-4 mt-4">
        <button
          className={`px-3 py-1 rounded border border-gray-300 bg-white hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="font-medium text-gray-700">
          Page {data.currentPage} of {data.totalPages}
        </span>
        <button
          className={`px-3 py-1 rounded border border-gray-300 bg-white hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={() =>
            setCurrentPage((p) => Math.min(data.totalPages, p + 1))
          }
          disabled={currentPage === data.totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CreditSpendsTable;
